import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import * as moment from 'moment/moment';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from 'src/app/components/shared-components/confirmation-dialog/confirmation-dialog.component';
import { SnackBarService } from 'src/app/components/shared-components/snack-bar/snack-bar.service';
import { DevicesDevicesService } from '../../../services/devices-services/devices-devices-services/devices-devices.service';
import {
  DevicesRegisterDeviceDialogComponent
} from "../devices-register-device-dialog/devices-register-device-dialog.component";
import {
  DeviceUpdateLicenseDialogComponent
} from "../device-update-license-dialog/device-update-license-dialog.component";

@Component({
  selector: 'app-device-resume-card',
  templateUrl: './device-resume-card.component.html',
  styleUrls: ['./device-resume-card.component.scss']
})
export class DeviceResumeCardComponent implements OnInit, OnDestroy {

  @Input() device;
  @Input() viewDisposition;
  updateDeviceStatusSubscription: Subscription;
  deleteDeviceSubscription: Subscription;
  updateDeviceMasterSubscription: Subscription;
  restartDeviceSubscription: Subscription;
  remoteAccessSubscription: Subscription;
  openingRemoteAccess = false;
  private remoteAccessWindow: Window | null = null;

  constructor(private router: Router,
    private devicesService: DevicesDevicesService,
    private snackBarService: SnackBarService,
    private dialog: MatDialog) { }

  ngOnInit() { }

  public toggleStatus() {
    this.updateDeviceStatusSubscription = this.devicesService.updateDeviceStatus(this.device.InternalId).pipe(
      tap(o => this.device.Active = o),
      catchError(error => { this.snackBarService.open(error['error']['message'], 'error'); return EMPTY; })
    ).subscribe();
  }

  public gotoDetails(e) {
    if ((e.target.className !== 'mat-icon notranslate material-icons mat-icon-no-color') && (e.target.className !== 'mat-icon-button') && (e.target.tagName !== 'SPAN')) {
      this.router.navigate(['/device', this.device.InternalId]);
    }
  }

  public setMaster(e?) {
    if (e) { e.stopPropagation(); }
    const master = !this.isMaster();
    this.updateDeviceMasterSubscription = this.devicesService.setMasterDevice(this.getDeviceInternalId(), master).pipe(
      tap(o => {
        this.device.Master = master;
        this.device.master = master;
        this.devicesService.refreshDevices();
      }),
      catchError(error => { this.snackBarService.open(error['error']['message'], 'error'); return EMPTY; })
    ).subscribe();
  }

  public isMaster() {
    return this.device && (this.device.Master === true || this.device.master === true);
  }

  private getDeviceInternalId() {
    return this.device.InternalId ? this.device.InternalId : this.device.internalId;
  }

  public deleteDevice() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'important',
        messages: ['devices-page.delete device', 'delete anyway']
      }
    });

    dialogRef.afterClosed().subscribe(action => {
      if (action) {
        this.deleteDeviceSubscription = this.devicesService.deleteDevice(this.device.InternalId).pipe(
          tap(o => { this.devicesService.refreshDevices(); }),
          catchError(error => { this.snackBarService.open(error['error']['message'], 'error'); return EMPTY; })
        ).subscribe();
      }
    });
  }

  public changeLicense() {
    const dialogref = this.dialog.open(DeviceUpdateLicenseDialogComponent, {
      data: {
        deviceInternalId: this.device.InternalId,
        licenseDescription: this.device.LicenseDescription
      }
    });
    dialogref.afterClosed().subscribe(
      (result) => {
        // this.selectedOption = result;
      }
    );
  }

  public restartDevice(e?, menuTrigger?: MatMenuTrigger) {
    if (e) { e.stopPropagation(); }
    if (menuTrigger) { menuTrigger.closeMenu(); }
    this.restartDeviceSubscription = this.devicesService.restartDevice(this.getDeviceInternalId()).pipe(
      tap(o => this.snackBarService.open(o['message'], 'success')),
      catchError(error => { this.snackBarService.open(error['error']['message'], 'error'); return EMPTY; })
    ).subscribe();
  }

  public openRemoteAccess(event?: MouseEvent, menuTrigger?: MatMenuTrigger) {
    if (event) { event.stopPropagation(); }
    if (menuTrigger) { menuTrigger.closeMenu(); }
    if (!this.device || this.device.RemoteAccessConfigured !== true || this.openingRemoteAccess) { return; }

    const internalId = this.getDeviceInternalId();
    if (!internalId) { return; }

    const remoteWindow = window.open('', '_blank');
    if (!remoteWindow) {
      this.snackBarService.open('devices-page.remote control popup blocked', 'error');
      return;
    }

    remoteWindow.opener = null;
    this.remoteAccessWindow = remoteWindow;
    this.openingRemoteAccess = true;
    this.remoteAccessSubscription = this.devicesService.launchRemoteAccess(internalId).pipe(
      tap(launchUrl => {
        remoteWindow.location.replace(launchUrl);
        this.remoteAccessWindow = null;
      }),
      catchError(error => {
        remoteWindow.close();
        this.remoteAccessWindow = null;
        const message = error && error.error && error.error.message;
        this.snackBarService.open(message && message !== 'Generic error' ? message : 'devices-page.remote control error', 'error');
        return EMPTY;
      }),
      finalize(() => this.openingRemoteAccess = false)
    ).subscribe();
  }

  public getStatus() {
    if (this.device) {
      switch (this.device.Status) {
        case 'ONLINE':
          return 'online';
        case 'WAITING_UPDATE':
          return 'waiting';
        case 'OFFLINE':
          return 'offline';
        default:
          return 'offline';
      }
    }
  }

  public isToday(now) {
    return moment().startOf('day').valueOf() < now && now < moment().endOf('day').valueOf();
  }

  ngOnDestroy() {
    if (this.updateDeviceStatusSubscription) { this.updateDeviceStatusSubscription.unsubscribe(); }
    if (this.deleteDeviceSubscription) { this.deleteDeviceSubscription.unsubscribe(); }
    if (this.updateDeviceMasterSubscription) { this.updateDeviceMasterSubscription.unsubscribe(); }
    if (this.restartDeviceSubscription) { this.restartDeviceSubscription.unsubscribe(); }
    if (this.remoteAccessSubscription) { this.remoteAccessSubscription.unsubscribe(); }
    if (this.remoteAccessWindow) { this.remoteAccessWindow.close(); }
  }


}
